import { Firm, Fund } from "@/app/types"
import { isString } from "@/app/utils"
import { Dispatch, SetStateAction } from "react"

export type EntityContextType = {
  entity: Entity | null
  setEntity: Dispatch<SetStateAction<Entity | null>>
}

// generic type
export class Entity {
  //TODO - change to # private properties
  private _entity: Fund | Firm
  private _isFund: boolean
  private _id: string

  constructor(entity: Fund | Firm) {
    if (!entity)
      throw new Error(
        `Invalid or null entity:${entity}(typeof ${typeof entity})`
      )

    // check for fundId first because firmId is present in both Fund & Firm objects
    if ("fundId" in entity && entity.fundId) {
      this._isFund = true
      this._entity = entity
      this._id = entity.fundId
      return // dont continue because the next check is true for both Fund and Firm
    }

    if ("firmId" in entity && entity.firmId) {
      this._isFund = false
      this._entity = entity
      this._id = entity.firmId
      return
    }

    throw new Error(`Unexpected entity:${entity}(typeof ${typeof entity})`)
  }
  get(): Fund | Firm {
    return this._entity
  }
  type(): string {
    return this.isFund() ? "Fund" : "Firm"
  }
  setName(): string {
    return this.isFund() ? "fundsets" : "firmsets"
  }
  propertyName(): string {
    return this.isFund() ? "fund" : "firm"
  }
  getFund(): Fund {
    return this.isFund() ? (this.get() as Fund) : null
  }
  getFirm(): Firm {
    return this.isFund() ? null : (this.get() as Firm)
  }
  isFund(): boolean {
    return this._isFund
  }
  isFirm(): boolean {
    return !this._isFund
  }
  getId(): string {
    return this._id
  }
  getName(): string {
    return this._entity.name
  }
  getRefId(): string {
    return this._entity.refId
  }
  getTicker(): string {
    return this.isFund() ? (this.get() as Fund).ticker : null
  }
  equals(other: Entity): boolean {
    return (
      other !== null && this._id == other._id && this._isFund == other._isFund
    )
  }
  toString(): string {
    return `(${this._isFund ? "fund" : "firm"} ${this._id}) ${this.getName()}`
  }
}

export namespace Entity {
  export const FUND_STRING = "fund"
  export const FIRM_STRING = "firm"
  export const FUND_OR_FIRM_STRINGS = [FUND_STRING, FIRM_STRING]

  export function isFundString(s: string): boolean {
    return isString(s) && s.toLowerCase() == FUND_STRING
  }
  export function isNotFundString(s: string): boolean {
    return !isFundString(s)
  }

  export function isFirmString(s: string): boolean {
    return isString(s) && s.toLowerCase() == FIRM_STRING
  }
  export function isNotFirmString(s: string): boolean {
    return !isFirmString(s)
  }

  export function isFirmOrFundString(s: string): boolean {
    return isFundString(s) || isFirmString(s)
  }
  export function isNotFirmOrFundString(s: string): boolean {
    return isNotFundString(s) && isNotFirmString(s)
  }
}
